import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { alphanumericDate, howOldTimeUpdater } from "../../constents/functions";
import {
  Job_Tag_Box_Primary,
  WhiteBoxWrapper,
} from "../../constents/components";

import locationIcon from "../../Assets/svgs/locationIcon.svg";
import salaryIcon from "../../Assets/svgs/salaryIcon.svg";
import candidateIcon from "../../Assets/svgs/candidateIcon.svg";
import jobIdIcon from "../../Assets/icons/jobIdIcon.svg";

import { QUESTION_GETTER } from "../../Utils/apis";

const STATUS_LABEL = {
  start: "In Review",
  applied: "Applied",
  "not selected": "Not Selected",
  eligible: "Shortlisted",
  "interview scheduled": "Interview Scheduled",
  "interview done": "Interview Submitted",
  "process completed": "Interview Submitted",
  "link expired": "Link Expired",
  under_consideration: "Under Consideration",
  sent_offer_letter: "Sent offer letter",
  rejected: "Rejected",
  hired: "Hired",
};

const Job = ({ job }) => {
  
  const jobTitle = job?.job_title;

  const companyName = job?.company_details?.company_name;

  const companyProfileImgUrl =
    job?.company_details?.company_profile ?? job?.company_details?.website_logo;

  const jobDescription =
    job?.about_job?.jd
      ?.replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim() || "";


  const jobType =
    job?.about_job?.job_type ?? job?.about_job?.type ?? job?.type ?? "";

  const minExperience =
    job?.about_job?.min_experience ??
    job?.about_job?.minExperience ??
    job?.min_experience ??
    job?.minExperience;

  const maxExperience =
    job?.about_job?.max_experience ??
    job?.about_job?.maxExperience ??
    job?.max_experience ??
    job?.maxExperience;


  const Min_Salary = job?.about_job?.min_salary;

  const Max_Salary = job?.about_job?.max_salary;


  const education = job?.about_job?.education ?? job?.education ?? [];

  const educationList = Array.isArray(education) ? education : [];

  const educationNames = educationList
    .map((item) => (typeof item === "string" ? item : item?.education))
    .filter(Boolean);


  const specializations = [
    ...new Set(
      educationList.flatMap((item) =>
        typeof item === "string"
          ? []
          : Array.isArray(item?.qualifications)
            ? item.qualifications
            : [],
      ),
    ),
  ];


  const skills = job?.about_job?.skills ?? job?.skills ?? [];

  const skillsList = Array.isArray(skills) ? skills : [];


  const location = Array.isArray(job?.location)
    ? job.location
    : Array.isArray(job?.address)
      ? job.address
      : [];


  const jobPostingTime = `Posted at ${alphanumericDate(job?.created_at)}`;

  const jobId = job?.job_id;

  const SortJobId = job?.short_jobid;

  const applicantCount = job?.total_applicants || 0;

  const highlights = [
    jobType || null,

    minExperience !== undefined && maxExperience !== undefined
      ? `${minExperience} - ${maxExperience} yrs Experience`
      : null,
  ].filter(Boolean);

  const currentStatus = job?.applicant_status?.toLowerCase();

  const appliedTime = job?.applied_date;

  const interviewLink =
    job?.interview_link !== "none" ? job?.interview_link : null;

  const [interviewButtonState, setInterviewButtonState] =
    React.useState("loading");

  React.useEffect(() => {
    if (currentStatus !== "interview scheduled" || !interviewLink) {
      return;
    }

    const params = new URLSearchParams(interviewLink.split("?")[1] ?? "");

    const applicantId = params.get("q");
    const interviewJobId = params.get("jobId");

    if (!applicantId || !interviewJobId) {
      setInterviewButtonState("start");
      return;
    }

    fetch(QUESTION_GETTER, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        applicantId,
        jobId: interviewJobId,
      }),
      credentials: "include",
    })
      .then((res) => res.json())
      .then((json) => {
        setInterviewButtonState(
          json.status === "SUCCESS" && json.data?.currentQuestion > 1
            ? "continue"
            : "start",
        );
      })
      .catch(() => setInterviewButtonState("start"));
  }, [currentStatus, interviewLink]);

  const Navigate = useNavigate();

  const goToJobDetailsPage = () => jobId && Navigate(`/jobs/id?jid=${jobId}`);

  const renderStatusBadge = () => {
    if (currentStatus === "interview scheduled") {
      return interviewLink ? (
        <Link to={interviewLink}>
          <span
            className={`cursor-pointer rounded-full text-white px-3 py-1 text-sm min-w-[100px] text-center ${
              interviewButtonState === "loading"
                ? "bg-gray-400"
                : interviewButtonState === "continue"
                  ? "bg-[#FF5D0E]"
                  : "bg-[#6320EE]"
            }`}
          >
            {interviewButtonState === "loading"
              ? "..."
              : interviewButtonState === "continue"
                ? "Continue Interview"
                : "Start Interview"}
          </span>
        </Link>
      ) : (
        <span className="rounded-full bg-black text-white px-3 py-1 text-sm min-w-[100px] text-center">
          Interview Scheduled
        </span>
      );
    }

    const label = STATUS_LABEL[currentStatus];

    return label ? (
      <span className="rounded-full bg-black text-white px-3 py-1 text-sm min-w-[100px] text-center">
        {label}
      </span>
    ) : null;
  };

  return (
    <WhiteBoxWrapper classes={"flex flex-col gap-2"}>

      <div className="flex gap-2">
        <img
          className="rounded-full h-14 aspect-square"
          src={companyProfileImgUrl}
          alt=""
        />

        <div className="flex-1">
          <h3 className="text-sm text-gray-500">{companyName}</h3>

          <h2
            className="text-lg font-medium text-black cursor-pointer hover:underline"
            onClick={goToJobDetailsPage}
          >
            {jobTitle}
          </h2>
        </div>

        <div className="flex items-center justify-end gap-2">
          {renderStatusBadge()}
        </div>
      </div>

      <div className="flex-1">
        {/* Highlights */}
        <div>
          <div className="flex flex-wrap gap-2 mt-1">
            {highlights.map((item, index) => (
              <Job_Tag_Box_Primary text={item} key={index} />
            ))}
          </div>
        </div>

        {/* Job ID */}
        {SortJobId && (
          <div className="flex gap-2 mt-2">
            <div className="flex items-center gap-2">
              <img src={jobIdIcon} alt="Job ID Icon" className="w-4 h-4" />
            </div>

            <p className="text-sm text-text-color-strong">{SortJobId}</p>
          </div>
        )}

        <div className="flex flex-col gap-3 py-2">

          <div className="flex gap-2">
            <img
              src={locationIcon}
              alt="Location Icon"
              className="w-4 h-4 mt-1"
            />

            <p className="text-sm text-text-color-strong">
              {location.length > 0
                ? location.join(", ")
                : "Location not specified"}
            </p>
          </div>

          <div className="flex gap-2">
            <img
              src={candidateIcon}
              alt="Candidates Icon"
              className="w-4 h-4 mt-1"
            />

            <p className="text-sm text-text-color-strong">
              {applicantCount}{" "}
              {applicantCount === 1 ? "Candidate" : "Candidates"} Applied
            </p>
          </div>

          <div className="flex gap-2">
            <img src={salaryIcon} alt="Salary Icon" className="w-4 h-4 mt-1" />

            {Min_Salary !== undefined &&
            Max_Salary !== undefined &&
            Number(Max_Salary) >= Number(Min_Salary) ? (
              <p className="text-sm text-text-color-strong">
                {Min_Salary} - {Max_Salary} / Year
              </p>
            ) : (
              <span className="text-sm text-text-color-strong">
                Salary not specified
              </span>
            )}
          </div>

          {educationNames.length > 0 && (
            <div>
              <p className="mb-1 text-sm font-medium text-text-color-strong">
                Education:
              </p>

              <div className="flex flex-wrap gap-2">
                {educationNames.map((item, index) => (
                  <Job_Tag_Box_Primary text={item} key={index} />
                ))}
              </div>
            </div>
          )}


          {specializations.length > 0 && (
            <div>
              <p className="mb-1 text-sm font-medium text-text-color-strong">
                Specialization:
              </p>

              <div className="flex flex-wrap gap-2">
                {specializations.map((item, index) => (
                  <Job_Tag_Box_Primary text={item} key={index} />
                ))}
              </div>
            </div>
          )}

          {skillsList.length > 0 && (
            <div>
              <p className="mb-1 text-sm font-medium text-text-color-strong">
                Skills:
              </p>

              <div className="flex flex-wrap gap-2">
                {skillsList.map((skill, index) => (
                  <Job_Tag_Box_Primary text={skill} key={index} />
                ))}
              </div>
            </div>
          )}

          <div className="flex-grow mb-4 text-xs text-gray-500 line-clamp-2">
            {jobDescription.slice(0, 200)}
            {jobDescription.length > 200 ? "..." : ""}
          </div>
        </div>



        <div className="flex items-center justify-between mt-auto">
          {!currentStatus && (
            <button
              onClick={goToJobDetailsPage}
              className="px-10 py-2 text-xs font-medium text-white bg-black rounded-full"
            >
              Apply Now
            </button>
          )}

          <div className="text-xs text-gray-400">
            {appliedTime && howOldTimeUpdater(appliedTime)}
          </div>

          <p className="flex justify-end text-xs md:text-sm">
            {jobPostingTime}
          </p>
        </div>
      </div>
    </WhiteBoxWrapper>
  );
};

export default Job;
